import { useResizeObserver } from "@vueuse/core";
import { ref } from "vue";

const BreakpointStates = {
  small: "isSmall",
  medium: "isMedium",
  large: "isLarge",
};
/**
 * A composable to get the current breakpoint state of a container.
 * The state is determined by the width of the container and the breakpoints passed to the composable.
 *
 * @param {HTMLElement} target - The target element to observe.
 * @param {Object} breakpoints - An object containing the breakpoints to use.
 * @param {number} breakpoints.small.min - The minimum width for the small breakpoint.
 * @param {number} breakpoints.small.max - The maximum width for the small breakpoint.
 * @param {number} breakpoints.medium.min - The minimum width for the medium breakpoint.
 * @param {number} breakpoints.medium.max - The maximum width for the medium breakpoint.
 * @param {number} breakpoints.large.min - The minimum width for the large breakpoint.
 * @param {number} breakpoints.large.max - The maximum width for the large breakpoint.
 *
 * Each breakpoint is an object with `min` and `max` properties that define the range of widths for that breakpoint.
 * The `min` and `max` properties are optional. If only one of them is provided, the other will be set to `undefined`.
 * If both are provided, the width must be within that range to be considered in that breakpoint state.
 *
 * None of the breakpoints are required, and if none are provided, the function will return an object with all states set to `false`.
 * They can be provided in any order and can be omitted if not needed.
 *
 * @returns {Object} An object containing the current breakpoint state.
 *
 * @typedef {Object} BreakpointState
 * @property {boolean} isSmall - Whether the container is in the small breakpoint state.
 * @property {boolean} isMedium - Whether the container is in the medium breakpoint state.
 * @property {boolean} isLarge - Whether the container is in the large breakpoint state.
 *
 * @example
 * const breakpoints = {
 *   small: { min: 0, max: 600 },
 *   medium: { min: 601, max: 1200 },
 *   large: { min: 1201, max: Infinity },
 * };
 * const { isSmall, isMedium, isLarge } = useContainerBreakpoints(target, breakpoints);
 *
 * @example
 * const breakpoints = {
 *   small: { min: 0 }
 * };
 * const { isSmall } = useContainerBreakpoints(target, breakpoints);
 */
export function useContainerBreakpoints(target, breakpoints) {
  const state = {
    [BreakpointStates.small]: ref(false),
    [BreakpointStates.medium]: ref(false),
    [BreakpointStates.large]: ref(false),
  };

  useResizeObserver(target, ([entry]) => {
    determineBreakpointState(entry.contentRect.width);
  });

  function determineBreakpointState(width) {
    for (const [key, conditions] of Object.entries(breakpoints)) {
      const stateKey = BreakpointStates[key];

      if (!stateKey && !conditions) {
        continue;
      }

      state[stateKey].value = evaluateBreakpointCondition(width, conditions);
    }
  }

  function evaluateBreakpointCondition(width, { min, max }) {
    if (min && max) {
      return width >= min && width <= max;
    }
    if (min) {
      return width >= min;
    }
    if (max) {
      return width <= max;
    }
  }

  return state;
}
